import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Calendar, dateFnsLocalizer, type SlotInfo, Views } from 'react-big-calendar';
import withDragAndDrop, {
  type EventInteractionArgs,
} from 'react-big-calendar/lib/addons/dragAndDrop';
import {
  type AppointmentUpdate,
  setEditingAppointmentId,
  useAppointments,
} from '@features/appointments';
import { useClinicians } from '@features/organizations';
import { Box } from '@mui/material';
import { useAppDispatch } from '@store/hooks';
import { format, getDay, parse, startOfWeek } from 'date-fns';
import { enUS } from 'date-fns/locale/en-US';

import { useOrgScheduleAccess } from '../../hooks/useOrgScheduleAccess';
import { useOrgScheduleFilters } from '../../hooks/useOrgScheduleFilters';
import { useScheduleReadOnly } from '../../hooks/useScheduleReadOnly';
import type { WeekStartDay } from '../../store/slice';
import AddAppointmentModal from '../AddAppointmentModal';
import { ProviderBadge } from '../ProviderBadge';

import CalendarContainer from './components/CalendarContainer';
import CalendarLoadingOverlay from './components/CalendarLoadingOverlay';
import {
  CALENDAR_V2_POPOVER_CLOSE_MS,
  CALENDAR_V2_POPOVER_OPEN_MS,
  CalendarV2AppointmentPopover,
  CalendarV2EventComponent,
  type CalendarV2PopoverAnchorReference,
} from './components/CalendarV2EventComponent';
import {
  CustomHeader,
  CustomMonthHeader,
  CustomTimeGutterHeader,
} from './components/CustomHeaders';
import { EventComponent } from './components/EventComponent';
import { denseOverlapLayout } from './denseOverlapLayout';
import { type CalendarEvent, type CalendarResource } from './types';
import {
  DEFAULT_ENCOUNTER_DURATION,
  getAppointmentDisplayTitle,
  getBigCalendarView,
  VALID_STEPS,
  VALID_TIMESLOTS,
} from './utils';

import '../../styles/calendar-custom.scss';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';

// Setup the localizer for the calendar. We rebuild it whenever the user's
// `weekStartDay` preference changes — react-big-calendar reads `firstOfWeek`
// from the localizer per render, so a fresh instance is the cleanest signal.
const locales = {
  'en-US': enUS,
};

const buildLocalizer = (weekStartDay: WeekStartDay) =>
  dateFnsLocalizer({
    format,
    parse,
    startOfWeek: (date: Date) => startOfWeek(date, { weekStartsOn: weekStartDay }),
    getDay,
    locales,
  });

// Create a drag and drop enabled calendar with proper typing
const DnDCalendar = withDragAndDrop<CalendarEvent>(Calendar);
const UNASSIGNED_RESOURCE_ID = '__unassigned__';

const createCalendarV2PopoverAnchorReference = (
  anchorEl: HTMLElement,
): CalendarV2PopoverAnchorReference => {
  const anchorRect = anchorEl.getBoundingClientRect();

  return {
    contextElement: anchorEl,
    getBoundingClientRect: () =>
      anchorEl.isConnected ? anchorEl.getBoundingClientRect() : anchorRect,
  };
};

const ProviderResourceHeader = ({
  resource,
}: {
  label: React.ReactNode;
  index: number;
  resource: object;
}) => {
  const calendarResource = resource as CalendarResource;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0.75,
        minWidth: 0,
        width: '100%',
        px: 1,
      }}
    >
      <ProviderBadge
        name={calendarResource.id === UNASSIGNED_RESOURCE_ID ? null : calendarResource.title}
        ariaLabel={calendarResource.title}
      />
      <Box
        component="span"
        sx={{
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          fontSize: 13,
          fontWeight: 650,
          color: '#2F344F',
        }}
      >
        {calendarResource.title}
      </Box>
    </Box>
  );
};

interface CalendarWidgetProps {
  onAppointmentUpdate: (
    appointmentId: string,
    updates: AppointmentUpdate,
    seriesId?: string,
  ) => void;
  useCalendarV2?: boolean;
  onAppointmentOpen?: (appointmentId: string) => void;
}

const CalendarWidget: React.FC<CalendarWidgetProps> = ({
  onAppointmentUpdate,
  useCalendarV2 = false,
  onAppointmentOpen,
}) => {
  // Get encounters data from context
  const {
    appointments,
    isLoading,
    isBackgroundLoading,
    viewMode,
    setViewMode,
    currentDate,
    setCurrentDate,
    calendarGranularity,
    weekStartDay,
    calendarVisibleHours,
    scrollToNowRequestId,
    refreshAppointments,
  } = useAppointments();

  const localizer = useMemo(() => buildLocalizer(weekStartDay), [weekStartDay]);

  // `min`/`max` from react-big-calendar are dates whose time-of-day defines the
  // visible scroll range. We anchor them to the calendar's current date so the
  // displayed bounds stay aligned with whatever day/week the user is on.
  const [minTime, maxTime] = useMemo(() => {
    const min = new Date(currentDate);
    min.setHours(calendarVisibleHours.startHour, 0, 0, 0);
    const max = new Date(currentDate);
    if (calendarVisibleHours.endHour >= 24) {
      // 24 means "midnight at end of day" — set to 23:59:59 to keep within the day.
      max.setHours(23, 59, 59, 999);
    } else {
      max.setHours(calendarVisibleHours.endHour, 0, 0, 0);
    }
    return [min, max];
  }, [currentDate, calendarVisibleHours.startHour, calendarVisibleHours.endHour]);

  const dispatch = useAppDispatch();
  const isReadOnly = useScheduleReadOnly();
  const { hasOrgScheduleAccess } = useOrgScheduleAccess();
  const { isOrgViewActive, selectedProviderIds } = useOrgScheduleFilters();
  const isOrgScheduleActive = hasOrgScheduleAccess && isOrgViewActive;
  const { clinicians } = useClinicians({ skip: !isOrgScheduleActive });

  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const calendarContainerRef = useRef<HTMLDivElement | null>(null);

  // Validate and sanitize calendar granularity to prevent invalid array length
  const safeStep = useMemo(() => {
    const step = calendarGranularity?.step;
    return VALID_STEPS.includes(step) ? step : 15; // Default to 15 if invalid
  }, [calendarGranularity?.step]);

  const safeTimeslots = useMemo(() => {
    const timeslots = calendarGranularity?.timeslots;
    return VALID_TIMESLOTS.includes(timeslots) ? timeslots : 4; // Default to 4 if invalid
  }, [calendarGranularity?.timeslots]);

  // Set initial time to scroll to. We want roughly 2 hours of context before
  // the current time, but never above the user's visible-hours window.
  const scrollToTime = new Date();
  scrollToTime.setHours(
    Math.max(calendarVisibleHours.startHour, scrollToTime.getHours() - 2),
    0,
    0,
    0,
  );

  // State for AddAppointmentModal
  const [isAddAppointmentModalOpen, setIsAddAppointmentModalOpen] = useState(false);
  const [selectedDateTime, setSelectedDateTime] = useState<Date | undefined>(undefined);
  const [selectedDuration, setSelectedDuration] = useState<number | undefined>(undefined);
  const [selectedProviderId, setSelectedProviderId] = useState<string | undefined>(undefined);
  const [openPopoverAppointmentId, setOpenPopoverAppointmentId] = useState<string | null>(null);
  const [openPopoverAnchorReference, setOpenPopoverAnchorReference] =
    useState<CalendarV2PopoverAnchorReference | null>(null);
  const popoverOpenTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const popoverCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isPopoverInteractionPinnedRef = useRef(false);

  const clearPopoverTimers = useCallback(() => {
    if (popoverOpenTimerRef.current) {
      clearTimeout(popoverOpenTimerRef.current);
      popoverOpenTimerRef.current = null;
    }
    if (popoverCloseTimerRef.current) {
      clearTimeout(popoverCloseTimerRef.current);
      popoverCloseTimerRef.current = null;
    }
  }, []);

  const closeAppointmentPopover = useCallback(() => {
    clearPopoverTimers();
    isPopoverInteractionPinnedRef.current = false;
    setOpenPopoverAppointmentId(null);
    setOpenPopoverAnchorReference(null);
  }, [clearPopoverTimers]);

  const schedulePopoverClose = useCallback(
    (appointmentId?: string) => {
      if (isPopoverInteractionPinnedRef.current) return;
      if (appointmentId && openPopoverAppointmentId && appointmentId !== openPopoverAppointmentId)
        return;

      if (popoverOpenTimerRef.current) {
        clearTimeout(popoverOpenTimerRef.current);
        popoverOpenTimerRef.current = null;
      }
      if (popoverCloseTimerRef.current) {
        clearTimeout(popoverCloseTimerRef.current);
      }
      popoverCloseTimerRef.current = setTimeout(() => {
        closeAppointmentPopover();
      }, CALENDAR_V2_POPOVER_CLOSE_MS);
    },
    [closeAppointmentPopover, openPopoverAppointmentId],
  );

  const handlePopoverMouseEnter = useCallback(
    (event: CalendarEvent, anchorEl: HTMLElement) => {
      const anchorReference = createCalendarV2PopoverAnchorReference(anchorEl);

      if (popoverCloseTimerRef.current) {
        clearTimeout(popoverCloseTimerRef.current);
        popoverCloseTimerRef.current = null;
      }

      if (openPopoverAppointmentId === event.resource.appointment_id) {
        setOpenPopoverAnchorReference(anchorReference);
        return;
      }

      if (openPopoverAppointmentId && !isPopoverInteractionPinnedRef.current) {
        if (popoverOpenTimerRef.current) {
          clearTimeout(popoverOpenTimerRef.current);
          popoverOpenTimerRef.current = null;
        }
        setOpenPopoverAppointmentId(event.resource.appointment_id);
        setOpenPopoverAnchorReference(anchorReference);
        return;
      }

      if (popoverOpenTimerRef.current) {
        clearTimeout(popoverOpenTimerRef.current);
      }
      popoverOpenTimerRef.current = setTimeout(() => {
        isPopoverInteractionPinnedRef.current = false;
        setOpenPopoverAppointmentId(event.resource.appointment_id);
        setOpenPopoverAnchorReference(anchorReference);
      }, CALENDAR_V2_POPOVER_OPEN_MS);
    },
    [openPopoverAppointmentId],
  );

  const handlePopoverInteractionPin = useCallback(() => {
    clearPopoverTimers();
    isPopoverInteractionPinnedRef.current = true;
  }, [clearPopoverTimers]);

  // Convert encounters to calendar events when encounters data changes
  useEffect(() => {
    const calendarEvents = appointments.map(appointment => {
      // Use start_time from new model
      const startTime = new Date(appointment.start_time);

      // Calculate end time based on duration
      const endTime = new Date(startTime);

      // Use duration from new model or default
      const durationMinutes = appointment.duration || DEFAULT_ENCOUNTER_DURATION;
      endTime.setMinutes(endTime.getMinutes() + durationMinutes);

      return {
        id: appointment.appointment_id,
        title: getAppointmentDisplayTitle(appointment),
        start: startTime,
        end: endTime,
        resourceId: appointment.provider_id ?? UNASSIGNED_RESOURCE_ID,
        resource: appointment,
      };
    });

    setEvents(calendarEvents);
  }, [appointments]);

  useEffect(() => clearPopoverTimers, [clearPopoverTimers]);

  const openPopoverEvent = useMemo(
    () =>
      openPopoverAppointmentId
        ? (events.find(event => event.resource.appointment_id === openPopoverAppointmentId) ?? null)
        : null,
    [events, openPopoverAppointmentId],
  );

  const resources = useMemo<CalendarResource[]>(() => {
    const byId = new Map<string, CalendarResource>();

    const visibleProviderIds =
      selectedProviderIds === null
        ? clinicians.map(clinician => clinician.userId)
        : selectedProviderIds;

    for (const providerId of visibleProviderIds ?? []) {
      const clinician = clinicians.find(candidate => candidate.userId === providerId);
      byId.set(providerId, {
        id: providerId,
        title: clinician?.name ?? 'Assigned provider',
      });
    }

    for (const appointment of appointments) {
      const id = appointment.provider_id ?? UNASSIGNED_RESOURCE_ID;
      if (selectedProviderIds !== null && !selectedProviderIds.includes(id)) continue;
      if (byId.has(id)) continue;
      byId.set(id, {
        id,
        title:
          appointment.provider?.name ??
          (appointment.provider_id ? 'Assigned provider' : 'Unassigned'),
      });
    }

    return Array.from(byId.values()).sort((a, b) => {
      if (a.id === UNASSIGNED_RESOURCE_ID) return 1;
      if (b.id === UNASSIGNED_RESOURCE_ID) return -1;
      return a.title.localeCompare(b.title);
    });
  }, [appointments, clinicians, selectedProviderIds]);

  const useProviderResources = useCalendarV2 && viewMode === 'day' && resources.length > 1;

  // Handle calendar date navigation - this is now handled by CalendarNavigation component
  const handleNavigate = useCallback(
    (newDate: Date) => {
      setCurrentDate(newDate.toISOString());
    },
    [setCurrentDate],
  );

  // Handle event movement (reschedule)
  const moveEvent = useCallback(
    async ({ event, start, end }: EventInteractionArgs<CalendarEvent>) => {
      try {
        const newStartTime = new Date(start);

        // Event has been moved - newStartTime contains the updated time

        // Update local state first for immediate feedback (optimistic update)
        const updatedEvents = events.map(existingEvent => {
          if (existingEvent.id === event.id) {
            return {
              ...existingEvent,
              start: new Date(start),
              end: new Date(end),
            };
          }
          return existingEvent;
        });

        setEvents(updatedEvents);

        // Prepare update data
        const updates: AppointmentUpdate = {
          start_time: newStartTime.toISOString(),
          duration: event.resource.duration,
        };

        // Always use onAppointmentUpdate with optional seriesId
        onAppointmentUpdate(event.id, updates, event.resource.series?.series_id);
      } catch (error) {
        console.error('Error updating encounter schedule:', error);
        // If there's an error, do a full refresh to ensure the UI is in sync with server
        await refreshAppointments();
      }
    },
    [events, refreshAppointments, onAppointmentUpdate],
  );

  // Handle event resize
  const resizeEvent = useCallback(
    async ({ event, start, end }: EventInteractionArgs<CalendarEvent>) => {
      try {
        // Calculate new duration in minutes
        const newStartTime = new Date(start);
        const newEndTime = new Date(end);
        const durationMinutes = Math.round(
          (newEndTime.getTime() - newStartTime.getTime()) / (1000 * 60),
        );

        // Update local state first for immediate feedback (optimistic update)
        const updatedEvents = events.map(existingEvent => {
          if (existingEvent.id === event.id) {
            return {
              ...existingEvent,
              start: new Date(start),
              end: new Date(end),
            };
          }
          return existingEvent;
        });

        setEvents(updatedEvents);

        // Prepare update data
        const updates: AppointmentUpdate = {
          start_time: newStartTime.toISOString(),
          duration: durationMinutes,
        };

        // Always use onAppointmentUpdate with optional seriesId
        onAppointmentUpdate(event.id, updates, event.resource.series?.series_id);
      } catch (error) {
        console.error('Error updating encounter duration:', error);
        // If there's an error, do a full refresh to ensure the UI is in sync with server
        await refreshAppointments();
      }
    },
    [events, refreshAppointments, onAppointmentUpdate],
  );

  const handleClickEvent = useCallback(
    (event: CalendarEvent) => {
      if (useCalendarV2 && onAppointmentOpen) {
        onAppointmentOpen(event.resource.appointment_id);
        return;
      }

      dispatch(setEditingAppointmentId(event.resource.appointment_id));
    },
    [dispatch, onAppointmentOpen, useCalendarV2],
  );

  // Wrapper for EventComponent with handlers
  const EventComponentWrapper = useCallback(
    ({ event, title }: { event: CalendarEvent; title: React.ReactNode }) => {
      if (useCalendarV2 && onAppointmentOpen) {
        return (
          <CalendarV2EventComponent
            event={event}
            viewMode={viewMode}
            onOpenAppointmentDetail={appointmentId => {
              closeAppointmentPopover();
              onAppointmentOpen(appointmentId);
            }}
            isPopoverOpen={openPopoverAppointmentId === event.resource.appointment_id}
            onPopoverMouseEnter={handlePopoverMouseEnter}
            onPopoverMouseLeave={schedulePopoverClose}
          />
        );
      }

      return <EventComponent event={event} title={title} viewMode={viewMode} />;
    },
    [
      handlePopoverMouseEnter,
      closeAppointmentPopover,
      onAppointmentOpen,
      openPopoverAppointmentId,
      schedulePopoverClose,
      useCalendarV2,
      viewMode,
    ],
  );

  // Handle selection slot (click and drag will capture start and end times)
  const handleSelectSlot = useCallback((slotInfo: SlotInfo) => {
    const startDay = new Date(slotInfo.start).setHours(0, 0, 0, 0);
    const endDay = new Date(slotInfo.end).setHours(0, 0, 0, 0);
    const resourceId = (slotInfo as SlotInfo & { resourceId?: string }).resourceId;

    // If selection spans multiple days, ignore it
    if (slotInfo.slots.length > 1 && startDay !== endDay) {
      return;
    }

    // Set the start time
    setSelectedDateTime(slotInfo.start);
    setSelectedProviderId(
      resourceId && resourceId !== UNASSIGNED_RESOURCE_ID ? resourceId : undefined,
    );

    // Calculate duration in minutes if there's a difference between start and end
    if (slotInfo.start && slotInfo.end) {
      const durationMinutes = Math.round(
        (slotInfo.end.getTime() - slotInfo.start.getTime()) / (1000 * 60),
      );

      // Only set duration if it's actually a drag selection (more than a few minutes)
      if (durationMinutes > 5) {
        setSelectedDuration(durationMinutes);
      } else {
        setSelectedDuration(undefined);
      }
    }

    setIsAddAppointmentModalOpen(true);
  }, []);

  // Handle close of AddAppointmentModal
  const handleCloseAddAppointmentModal = useCallback(() => {
    setIsAddAppointmentModalOpen(false);
    setSelectedDateTime(undefined);
    setSelectedDuration(undefined);
    setSelectedProviderId(undefined);
  }, []);

  // Add handler for encounter creation
  const handleAppointmentCreated = useCallback(() => {
    // Refresh encounters to show the new one
    refreshAppointments();
  }, [refreshAppointments]);

  useEffect(() => {
    if (scrollToNowRequestId === 0 || viewMode === 'month') return;

    let animationFrameId: number | null = null;

    const scrollToNow = () => {
      const scrollContainer =
        calendarContainerRef.current?.querySelector<HTMLElement>('.rbc-time-content');
      if (!scrollContainer) return;

      const now = new Date();
      const contextMinutes = 120;
      const targetMinutes = Math.max(0, now.getHours() * 60 + now.getMinutes() - contextMinutes);
      const maxScrollTop = Math.max(0, scrollContainer.scrollHeight - scrollContainer.clientHeight);
      const targetScrollTop = Math.min(
        maxScrollTop,
        (targetMinutes / (24 * 60)) * scrollContainer.scrollHeight,
      );

      scrollContainer.scrollTo({
        top: targetScrollTop,
        behavior: 'smooth',
      });
    };

    animationFrameId = window.requestAnimationFrame(scrollToNow);

    return () => {
      if (animationFrameId !== null) {
        window.cancelAnimationFrame(animationFrameId);
      }
    };
  }, [scrollToNowRequestId, safeStep, safeTimeslots, viewMode]);

  // eventPropGetter removed - EventComponent now owns all styling

  return (
    <CalendarContainer ref={calendarContainerRef}>
      <CalendarLoadingOverlay isLoading={isLoading} isBackgroundLoading={isBackgroundLoading} />

      <DnDCalendar
        localizer={localizer}
        events={events}
        resources={useProviderResources ? resources : undefined}
        resourceAccessor="resourceId"
        resourceIdAccessor={(resource: object) => (resource as CalendarResource).id}
        resourceTitleAccessor={(resource: object) => (resource as CalendarResource).title}
        defaultView={getBigCalendarView(viewMode)}
        view={getBigCalendarView(viewMode)}
        views={[Views.DAY, Views.WEEK, Views.MONTH]}
        step={safeStep}
        showMultiDayTimes
        date={currentDate}
        onNavigate={handleNavigate}
        onView={setViewMode}
        scrollToTime={scrollToTime}
        min={minTime}
        max={maxTime}
        onSelectEvent={isReadOnly ? undefined : handleClickEvent}
        onEventDrop={isReadOnly ? undefined : moveEvent}
        onEventResize={isReadOnly ? undefined : resizeEvent}
        selectable={!isReadOnly}
        onSelectSlot={isReadOnly ? undefined : handleSelectSlot}
        resizable={!isReadOnly}
        resizableAccessor={isReadOnly ? () => false : () => viewMode !== 'month'}
        draggableAccessor={isReadOnly ? () => false : undefined}
        tooltipAccessor={null} // Disable native browser tooltip (ExpandedCard shows details)
        style={{ height: '100%' }}
        timeslots={safeTimeslots}
        dayLayoutAlgorithm={
          useCalendarV2 && viewMode !== 'month' ? denseOverlapLayout : 'no-overlap'
        }
        toolbar={false} // Hide the default toolbar since we have our own navigation
        components={{
          event: EventComponentWrapper,
          ...(useProviderResources && {
            resourceHeader: ProviderResourceHeader,
          }),
          week: {
            header: CustomHeader,
          },
          month: {
            header: CustomMonthHeader,
          },
          ...(viewMode === 'day' && {
            timeGutterHeader: () => <CustomTimeGutterHeader currentDate={currentDate} />,
          }),
        }}
      />

      {useCalendarV2 && onAppointmentOpen && openPopoverEvent && openPopoverAnchorReference && (
        <CalendarV2AppointmentPopover
          event={openPopoverEvent}
          anchorReference={openPopoverAnchorReference}
          open
          onClose={closeAppointmentPopover}
          onPointerEnter={clearPopoverTimers}
          onPointerLeave={() => schedulePopoverClose(openPopoverEvent.resource.appointment_id)}
          onInteractionPin={handlePopoverInteractionPin}
          onOpenAppointmentDetail={onAppointmentOpen}
        />
      )}

      {/* Add Appointment Modal - hidden in read-only mode */}
      {!isReadOnly && (
        <AddAppointmentModal
          open={isAddAppointmentModalOpen}
          onClose={handleCloseAddAppointmentModal}
          initialScheduledTime={selectedDateTime}
          initialDuration={selectedDuration}
          initialProviderId={selectedProviderId}
          onAppointmentCreated={handleAppointmentCreated}
        />
      )}
    </CalendarContainer>
  );
};

export default CalendarWidget;
